<template>
  <ContentWrap>
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="80px"
    >
      <el-form-item label="状态" prop="status">
        <el-select
          v-model="queryParams.status"
          class="!w-200px"
          clearable
          placeholder="请选择状态"
        >
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="服务类型" prop="serviceType">
        <el-select
          v-model="queryParams.serviceType"
          class="!w-200px"
          clearable
          placeholder="请选择服务类型"
        >
          <el-option
            v-for="item in serviceTypeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="联系电话" prop="contactPhone">
        <el-input
          v-model="queryParams.contactPhone"
          class="!w-200px"
          clearable
          placeholder="请输入联系电话"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column align="center" label="服务" prop="serviceName" min-width="110">
        <template #default="scope">
          <el-tag type="info">{{ scope.row.serviceName || scope.row.serviceType }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="联系人" prop="contactName" width="110" />
      <el-table-column align="center" label="联系电话" prop="contactPhone" width="130" />
      <el-table-column align="center" label="需求说明" prop="details" min-width="240" show-overflow-tooltip />
      <el-table-column align="center" label="状态" prop="status" width="100">
        <template #default="scope">
          <el-tag v-if="scope.row.status === 'PENDING'" type="warning">处理中</el-tag>
          <el-tag v-else-if="scope.row.status === 'CONFIRMED'" type="primary">已确认</el-tag>
          <el-tag v-else-if="scope.row.status === 'COMPLETED'" type="success">已完成</el-tag>
          <el-tag v-else-if="scope.row.status === 'CANCELLED'" type="info">已取消</el-tag>
          <el-tag v-else>{{ scope.row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="提交时间"
        prop="createTime"
        width="180"
      />
      <el-table-column align="center" label="操作" width="180">
        <template #default="scope">
          <el-button
            v-if="scope.row.status === 'PENDING'"
            v-hasPermi="['hotel:concierge:update']"
            link
            type="primary"
            @click="handleUpdateStatus(scope.row, 'CONFIRMED')"
          >
            确认
          </el-button>
          <el-button
            v-if="scope.row.status === 'CONFIRMED'"
            v-hasPermi="['hotel:concierge:update']"
            link
            type="success"
            @click="handleUpdateStatus(scope.row, 'COMPLETED')"
          >
            完成
          </el-button>
          <el-button
            v-if="scope.row.status === 'PENDING' || scope.row.status === 'CONFIRMED'"
            v-hasPermi="['hotel:concierge:update']"
            link
            type="warning"
            @click="handleUpdateStatus(scope.row, 'CANCELLED')"
          >
            取消
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import * as conciergeApi from '@/api/hotel/concierge'

defineOptions({ name: 'HotelConcierge' })

const message = useMessage()

const loading = ref(true)
const total = ref(0)
const list = ref<any[]>([])

const statusOptions = [
  { label: '处理中', value: 'PENDING' },
  { label: '已确认', value: 'CONFIRMED' },
  { label: '已完成', value: 'COMPLETED' },
  { label: '已取消', value: 'CANCELLED' }
]

const serviceTypeOptions = [
  { label: '机场接送', value: 'AIRPORT_PICKUP' },
  { label: '餐厅预订', value: 'RESTAURANT_BOOKING' },
  { label: '旅游向导', value: 'TOUR_GUIDE' },
  { label: '行李寄存', value: 'LUGGAGE_STORAGE' },
  { label: '叫车服务', value: 'TAXI_CALLING' },
  { label: '叫醒服务', value: 'WAKE_UP_CALL' },
  { label: '洗衣服务', value: 'LAUNDRY' },
  { label: '票务预订', value: 'TICKET_BOOKING' }
]

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 20,
  status: undefined,
  serviceType: undefined,
  contactPhone: undefined
})
const queryFormRef = ref()

const getList = async () => {
  loading.value = true
  try {
    const data = await conciergeApi.getConciergePage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value.resetFields()
  handleQuery()
}

const statusActionLabel: Record<string, string> = {
  CONFIRMED: '确认',
  COMPLETED: '完成',
  CANCELLED: '取消'
}

const handleUpdateStatus = async (row: any, status: string) => {
  try {
    await message.confirm(`确认${statusActionLabel[status]}该礼宾服务请求？`)
    await conciergeApi.updateConciergeStatus(row.id, status)
    message.success('操作成功')
    await getList()
  } catch {}
}

onMounted(() => {
  getList()
})
</script>
